import React from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Camera, Lightbulb, Image as ImageIcon, Check, X } from 'lucide-react';

export default function ImageryStyle() {
  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-4xl md:text-5xl font-display font-normal text-jackstone-blue mb-4">
          Imagery & Photography Style
        </h1>
        <p className="text-base text-body leading-relaxed">
          Our imagery showcases industrial precision, technical excellence, and real-world applications.
        </p>
      </div>

      <Card className="p-8 bg-surface">
        <h2 className="text-2xl font-heading font-semibold text-jackstone-blue mb-4">
          Photography Direction
        </h2>
        <p className="text-body mb-6">
          Jackstone imagery should emphasize technical detail, industrial environments, and the
          quality of our manufacturing processes.
        </p>
        <div className="grid md:grid-cols-3 gap-4">
          <div className="bg-navy p-6 rounded-lg text-center">
            <Camera className="h-12 w-12 text-white mx-auto mb-3" />
            <h3 className="text-lg font-heading font-semibold text-white mb-2">Technical</h3>
            <p className="text-sm text-white/80">
              Sharp focus on machinery, precision components, industrial details
            </p>
          </div>
          <div className="bg-accent-teal p-6 rounded-lg text-center">
            <Lightbulb className="h-12 w-12 text-white mx-auto mb-3" />
            <h3 className="text-lg font-heading font-semibold text-white mb-2">Authentic</h3>
            <p className="text-sm text-white/90">
              Real workshop environments, actual manufacturing processes
            </p>
          </div>
          <div className="bg-jackstone-blue p-6 rounded-lg text-center">
            <ImageIcon className="h-12 w-12 text-white mx-auto mb-3" />
            <h3 className="text-lg font-heading font-semibold text-white mb-2">Professional</h3>
            <p className="text-sm text-white/80">
              Clean compositions, balanced lighting, industrial aesthetic
            </p>
          </div>
        </div>
      </Card>

      <div>
        <h2 className="text-2xl font-heading font-semibold text-jackstone-blue mb-4">
          Image Categories
        </h2>
        <div className="grid md:grid-cols-2 gap-6">
          <Card className="p-6">
            <Badge className="mb-3">Product Photography</Badge>
            <h3 className="text-lg font-heading font-semibold text-body mb-2">
              Equipment & Machinery
            </h3>
            <ul className="space-y-2 text-sm text-subtle">
              <li>• High-quality 3D renders or professional photographs</li>
              <li>• Clean backgrounds (white, light gray, or industrial setting)</li>
              <li>• Multiple angles showing key features</li>
              <li>• Proper lighting to showcase metal finishes</li>
              <li>• Detail shots of precision components</li>
            </ul>
          </Card>

          <Card className="p-6">
            <Badge className="mb-3">Manufacturing Photography</Badge>
            <h3 className="text-lg font-heading font-semibold text-body mb-2">
              Workshop & Process
            </h3>
            <ul className="space-y-2 text-sm text-subtle">
              <li>• Real workshop environments and machinery</li>
              <li>• Close-ups of manufacturing processes</li>
              <li>• CNC machining, welding, assembly work</li>
              <li>• Natural industrial lighting with slight enhancement</li>
              <li>• Safety equipment visible where appropriate</li>
            </ul>
          </Card>

          <Card className="p-6">
            <Badge className="mb-3">Application Photography</Badge>
            <h3 className="text-lg font-heading font-semibold text-body mb-2">
              In-Use & Installation
            </h3>
            <ul className="space-y-2 text-sm text-subtle">
              <li>• Equipment installed in customer facilities</li>
              <li>• Food processing environments</li>
              <li>• Scale reference (people working with equipment)</li>
              <li>• Before/after product quality demonstrations</li>
              <li>• Wide shots showing full system integration</li>
            </ul>
          </Card>

          <Card className="p-6">
            <Badge className="mb-3">Detail Photography</Badge>
            <h3 className="text-lg font-heading font-semibold text-body mb-2">
              Components & Materials
            </h3>
            <ul className="space-y-2 text-sm text-subtle">
              <li>• Macro shots of welds, fittings, precision parts</li>
              <li>• Material quality and finish details</li>
              <li>• Brushed aluminum, stainless steel textures</li>
              <li>• Hydraulic components and connections</li>
              <li>• Quality control and inspection processes</li>
            </ul>
          </Card>
        </div>
      </div>

      <Card className="p-6">
        <h3 className="text-xl font-heading font-semibold text-jackstone-blue mb-4">
          Lighting & Composition
        </h3>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-medium text-body mb-2">Lighting Style</h4>
            <ul className="space-y-2 text-sm text-subtle">
              <li className="flex items-start gap-2"><Check className="h-4 w-4 text-success flex-shrink-0 mt-0.5" /><span>Natural and balanced industrial lighting</span></li>
              <li className="flex items-start gap-2"><Check className="h-4 w-4 text-success flex-shrink-0 mt-0.5" /><span>Slight enhancement to showcase metallic finishes</span></li>
              <li className="flex items-start gap-2"><Check className="h-4 w-4 text-success flex-shrink-0 mt-0.5" /><span>Even illumination without harsh shadows</span></li>
              <li className="flex items-start gap-2"><Check className="h-4 w-4 text-success flex-shrink-0 mt-0.5" /><span>Cool colour temperature (5000-6500K) for technical feel</span></li>
            </ul>
          </div>
          <div>
            <h4 className="font-medium text-body mb-2">Composition Rules</h4>
            <ul className="space-y-2 text-sm text-subtle">
              <li className="flex items-start gap-2"><Check className="h-4 w-4 text-success flex-shrink-0 mt-0.5" /><span>Clean, uncluttered backgrounds</span></li>
              <li className="flex items-start gap-2"><Check className="h-4 w-4 text-success flex-shrink-0 mt-0.5" /><span>Rule of thirds for dynamic shots</span></li>
              <li className="flex items-start gap-2"><Check className="h-4 w-4 text-success flex-shrink-0 mt-0.5" /><span>Sufficient negative space</span></li>
              <li className="flex items-start gap-2"><Check className="h-4 w-4 text-success flex-shrink-0 mt-0.5" /><span>Straight horizon lines and vertical alignment</span></li>
            </ul>
          </div>
        </div>
      </Card>

      <Card className="p-6 bg-light-blue border-jackstone-blue/20">
        <h3 className="text-xl font-heading font-semibold text-jackstone-blue mb-4">
          Images to Use
        </h3>
        <div className="grid sm:grid-cols-2 gap-4 mb-4">
          <div className="flex items-start gap-2">
            <Check className="h-5 w-5 text-success flex-shrink-0 mt-0.5" />
            <span className="text-sm text-subtle">Professional product photography</span>
          </div>
          <div className="flex items-start gap-2">
            <Check className="h-5 w-5 text-success flex-shrink-0 mt-0.5" />
            <span className="text-sm text-subtle">Real manufacturing processes</span>
          </div>
          <div className="flex items-start gap-2">
            <Check className="h-5 w-5 text-success flex-shrink-0 mt-0.5" />
            <span className="text-sm text-subtle">Industrial environments</span>
          </div>
          <div className="flex items-start gap-2">
            <Check className="h-5 w-5 text-success flex-shrink-0 mt-0.5" />
            <span className="text-sm text-subtle">Technical close-ups</span>
          </div>
        </div>
      </Card>

      <Card className="p-6 border-destructive/50 bg-destructive/5">
        <h3 className="text-xl font-heading font-semibold text-jackstone-blue mb-4">
          Images to Avoid
        </h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="flex items-start gap-2">
            <X className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
            <span className="text-sm text-subtle">Generic stock photography</span>
          </div>
          <div className="flex items-start gap-2">
            <X className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
            <span className="text-sm text-subtle">Overly staged or artificial scenes</span>
          </div>
          <div className="flex items-start gap-2">
            <X className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
            <span className="text-sm text-subtle">Low-resolution or blurry images</span>
          </div>
          <div className="flex items-start gap-2">
            <X className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
            <span className="text-sm text-subtle">Heavy filters or effects</span>
          </div>
        </div>
      </Card>
    </div>
  );
}
